import { type StudyRunCaseDashboardMissionDto } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { getStudyThreadKillChainLabel } from '@utils/meia-enum.util';
import { type FC, useMemo } from 'react';
import { formatDashboardScore } from './meia-p4-run-cases.util';
import { getBriefStudyVerdict } from './meia-p5-brief.util';
import { getScoringBandLabel, getScoringBandTone } from './meia-scoring-band.util';
import { getStudyThreadRoleLabel } from './meia-thread-role.util';

type MeiaP4KillwebVerdictTableProps = {
  missions: StudyRunCaseDashboardMissionDto[];
  onInspect: (params: { missionId: string; threadId: string }) => void;
};

export const MeiaP4KillwebVerdictTable: FC<MeiaP4KillwebVerdictTableProps> = (props) => {
  const { missions, onInspect } = props;

  const rows = useMemo(
    () =>
      missions.flatMap((mission) =>
        mission.threadVerdicts.map((verdict) => ({
          missionId: mission.missionId,
          missionName: mission.missionName,
          verdict,
        })),
      ),
    [missions],
  );
  const studyVerdict = useMemo(() => getBriefStudyVerdict(missions), [missions]);

  if (rows.length === 0) return null;

  return (
    <Tile
      contentClassName="gap-3"
      header={{
        actions: <span className="text-xs text-gray-400">Study verdict = worst-case thread</span>,
        title: 'Kill-Web Verdict',
      }}
    >
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-left text-xs">
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr>
              <th className="px-3 py-2 font-medium text-gray-700">Thread</th>
              <th className="px-3 py-2 font-medium text-gray-700">Pattern</th>
              <th className="px-3 py-2 font-medium text-gray-700">Role</th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">Score</th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">Verdict</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                className="border-b border-gray-100 last:border-b-0"
                key={row.verdict.threadId}
              >
                <td className="px-3 py-2">
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate font-medium text-gray-900">{row.verdict.threadName}</span>
                    {missions.length > 1 && (
                      <span className="truncate text-[11px] text-gray-400">{row.missionName}</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-gray-600">
                  {getStudyThreadKillChainLabel(row.verdict.killChainCategory)}
                </td>
                <td className="px-3 py-2 text-gray-600">{getStudyThreadRoleLabel(row.verdict.threadRole)}</td>
                <td className="px-3 py-2 text-center font-mono text-gray-900">
                  {formatDashboardScore(row.verdict.score)}
                </td>
                <td className="px-3 py-2 text-center">
                  <Badge
                    size="xs"
                    variant={getScoringBandTone(row.verdict.band).badgeVariant}
                  >
                    {getScoringBandLabel(row.verdict.band)}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button
                    onClick={() => onInspect({ missionId: row.missionId, threadId: row.verdict.threadId })}
                    size="xs"
                    type="button"
                    variant="link"
                  >
                    Inspect
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-gray-200 bg-gray-50">
            <tr>
              <td
                className="px-3 py-2 font-semibold text-gray-900"
                colSpan={3}
              >
                Study (worst-case rollup)
              </td>
              <td className="px-3 py-2 text-center font-mono font-semibold text-gray-900">
                {formatDashboardScore(studyVerdict.score)}
              </td>
              <td className="px-3 py-2 text-center">
                <Badge
                  size="xs"
                  variant={getScoringBandTone(studyVerdict.band).badgeVariant}
                >
                  {getScoringBandLabel(studyVerdict.band)}
                </Badge>
              </td>
              <td className="px-3 py-2" />
            </tr>
          </tfoot>
        </table>
      </div>
    </Tile>
  );
};
